import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton, MenuItem,
  MenuList,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DataTable } from "../../../../components/DataTable";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  calculateDataTableState, constructQueryParamsString,
  getSortDirection, handleMultiLineImportErrors,
  handlePageSizeChange,
  showErrorToast,
  showSuccessToast
} from "../../../../utils";
import {
  supplierAdministrationKeys,
  useCreateSupplierLocation,
  useDeleteSupplierLocation,
  useDownloadSupplierLocationsTemplate,
  useGetSupplierLocations,
  useImportSupplierLocations,
  useUpdateSupplierLocation
} from "../../../../hooks/query/supplier-administration";
import { FaBars, FaPen, FaTrash } from "react-icons/fa";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { SourcingLocationModal } from "./index";
import { ImportData } from "../../../../components/ImportData";

export const spendAmountsYearsKeys = [2021, 2022, 2023, 2024];

function ManufacturingOrSourcingLocations() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierManagement.manufacturingOrSourcingLocations"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const buttonsContainerFlow = useBreakpointValue({ base: "column", md: "row" });

  const [locationsList, setLocationsList] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [locationModalData, setLocationModalData] = useState(null);

  const {
    isOpen: isConfirmDialogOpen,
    onOpen: onConfirmDialogOpen,
    onClose: onConfirmDialogClose
  } = useDisclosure();
  const {
    isOpen: isLocationModalOpen,
    onOpen: onLocationModalOpen,
    onClose: onLocationModalClose
  } = useDisclosure();

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const getSpendColumnsAccessors = () => {
      return spendAmountsYearsKeys.map(yearKey => {
        return { originalAccessor: `amountSpend${yearKey}`, alternativeAccessor: `totalSpend${yearKey}` };
      });
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject,
          sortAccessors: [
            { originalAccessor: "supplier", alternativeAccessor: "supplier.name" },
            { originalAccessor: "location", alternativeAccessor: "country" },
            ...getSpendColumnsAccessors()
          ]
        })
      };
    });
  };

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const getAmountSpendAttributes = (el) => {
    const spendObj = {};
    spendAmountsYearsKeys.forEach(yearKey => {
      spendObj[`amountSpend${yearKey}`] = el.spentAmounts?.find(sp => sp.year === yearKey)?.amount ?? 0;
    });
    return spendObj;
  };

  const {
    isFetching: isFetchingLocations,
    refetch: refetchLocations
  } = useGetSupplierLocations(getQueryParamsString(), {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const locations = pageContent.map((el) => {
          return {
            id: el.id,
            supplier: el.supplier,
            location: el.location,
            industryCode: el.industryCode,
            spentAmounts: el.spentAmounts,
            ...getAmountSpendAttributes(el)
          };
        });
        setLocationsList(locations);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const handleAddNewLocation = () => {
    onLocationModalOpen();
  };

  const closeLocationModal = () => {
    setLocationModalData(null);
    onLocationModalClose();
  };

  const getRequestConfig = (successMessage) => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        if (isLocationModalOpen) {
          closeLocationModal();
        }
        showSuccessToast(toast, successMessage);
        queryClient.invalidateQueries(supplierAdministrationKeys.supplierLocations());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner())
    };
  };

  const { mutate: createLocation } = useCreateSupplierLocation(getRequestConfig(t("messages.locationCreated")));
  const { mutate: updateLocation } = useUpdateSupplierLocation(getRequestConfig(t("messages.locationUpdated")));

  const columns = useMemo(() => {
      function handleEditLocation(data) {
        setLocationModalData(data.row.original);
        onLocationModalOpen();
      }

      function handleDelete(rowId) {
        setSelectedRowId(rowId);
        onConfirmDialogOpen();
      }

      function getSpendByYearColumns() {
        return spendAmountsYearsKeys.map(yearKey => {
          return {
            header: t("gridHeaders.amountSpend", { year: yearKey }),
            accessorKey: `amountSpend${yearKey}`,
            size: 130,
            sortDirection: getSortDirection(queryPageSortBy, `totalSpend${yearKey}`),
            enableColumnFilter: false
          };
        });
      }

      return [
        {
          header: t("gridHeaders.supplier"),
          accessorKey: "supplier",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.name")
        },
        {
          header: t("gridHeaders.location"),
          accessorKey: "location",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "country")
        },
        {
          header: t("gridHeaders.industryCode"),
          accessorKey: "industryCode",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "industryCode")
        },
        ...getSpendByYearColumns(),
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 90,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleEditLocation(row)}
                >
                  {t("buttons.editLocation")}
                </MenuItem>
                <MenuItem
                  icon={<FaTrash color="red" opacity="0.6" />}
                  onClick={() => handleDelete(row.row.original.id)}
                >
                  {t("buttons.deleteLocation")}
                </MenuItem>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, onLocationModalOpen, onConfirmDialogOpen]
  );

  const { mutate: deleteLocation } = useDeleteSupplierLocation({
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.locationDeleted"));
      queryClient.invalidateQueries(supplierAdministrationKeys.supplierLocations());
      refetchLocations();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const onDelete = () => {
    if (selectedRowId !== null) {
      dispatch(showAppSpinner());
      deleteLocation(selectedRowId);
    }
  };

  const handleLocationModalSubmit = (id, data) => {
    if (typeof id !== "undefined") {
      updateLocation({ id, data });
    } else {
      createLocation(data);
    }
  };

  return (
    <Box marginBlockStart="5">
      <Flex
        marginBlockEnd="5"
        justifyContent={"space-between"}
        flexFlow={buttonsContainerFlow}
        gap={buttonsContainerFlow === "column" ? 2 : 0}
      >
        <Button
          colorScheme="customScheme"
          width="40"
          onClick={handleAddNewLocation}
        >
          + {t("buttons.addNewLocation")}
        </Button>
        <ImportData
          importDataBtnId={"import-locations-data"}
          importDataHook={useImportSupplierLocations}
          importDataConfig={{
            onSuccess: () => {
              showSuccessToast(toast, globalTranslation("messages.importSuccess"));
              queryClient.invalidateQueries(supplierAdministrationKeys.supplierLocations());
            },
            onError: err => handleMultiLineImportErrors(err, toast),
            onSettled: () => dispatch(hideAppSpinner())
          }}
          downloadTemplateHook={useDownloadSupplierLocationsTemplate}
        />
      </Flex>

      {isLocationModalOpen && <SourcingLocationModal
        modalData={locationModalData}
        isOpen={isLocationModalOpen}
        onClose={closeLocationModal}
        onSubmit={handleLocationModalSubmit}
      />}

      <DataTable
        data={locationsList}
        columns={columns}
        isLoading={isFetchingLocations}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => manageDataTableState({
          filterObj: newFilterObj,
          queryPageIndex: 0
        })}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />

      <DeleteDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onDelete={onDelete}
        entityName={"Location"}
      />
    </Box>
  );
}

export default ManufacturingOrSourcingLocations;